<script>
import { getStorageList, updateStorageStatus } from '@/api/storage.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'StorageManagementPage',
  data() {
    return {
      loading: false,
      summary: {},
      items: [],
      savingCode: '',
      adminInfo: getAdminInfo(),
      activeStorageTab: 'requests',
      requestStatusFilter: 'ALL',
      statusDialog: {
        open: false,
        item: null,
        status: '',
        memo: '',
      },
    }
  },
  mounted() {
    this.load()
  },
  computed: {
    storedItems() {
      return this.items.filter(item => item.storageStatus === 'STORED' && !item.storageRequestCode)
    },
    requestItems() {
      return this.items.filter(item => item.storageStatus !== 'STORED' || item.storageRequestCode)
    },
    filteredRequestItems() {
      return this.filterRequestItems(this.requestStatusFilter)
    },
    hasAnyItems() {
      return this.storedItems.length > 0 || this.requestItems.length > 0
    },
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const { data } = await getStorageList()
        if (data.code === 0) {
          this.summary = data.summary || {}
          this.items = data.items || []
        }
      } finally {
        this.loading = false
      }
    },
    fmtNum(value) {
      return Number(value || 0).toLocaleString('ko-KR')
    },
    fmtWon(value) {
      return `${Number(value || 0).toLocaleString('ko-KR')}원`
    },
    fmtDate(value) {
      return value ? String(value).slice(0, 10) : '-'
    },
    fmtDateTime(value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
    },
    statusLabel(value) {
      return {
        STORED: '보관중',
        PICKUP_REQUESTED: '수령신청',
        DELIVERY_REQUESTED: '배송신청',
        TRANSFERRING: '이동중',
        READY_FOR_PICKUP: '수령준비완료',
        PICKED_UP: '수령완료',
        SHIPPING: '배송중',
        DELIVERED: '배송완료',
        CANCELED: '취소',
      }[value] || value || '-'
    },
    editableStatusLabel(value, item) {
      if (value === 'READY_FOR_PICKUP' && this.isReceivingBranch(item)) return '지점 수령완료'
      return this.statusLabel(value)
    },
    paymentStatusLabel(value) {
      return {
        REQUESTED: '입금대기',
        WAITING: '입금대기',
        PAID: '결제완료',
        EXPIRED: '입금만료',
        CANCELED: '취소',
        REFUNDED: '환불완료',
      }[value] || value || '-'
    },
    paymentTypeLabel(value) {
      return {
        CASH: '계좌이체',
        BANK: '계좌이체',
        BANK_TRANSFER: '계좌이체',
        CARD: '카드결제',
      }[value] || value || '-'
    },
    requestTypeLabel(value) {
      return value === 'DELIVERY' ? '배송' : value === 'PICKUP' ? '방문수령' : '-'
    },
    transferStatusLabel(value) {
      return {
        REQUESTED: '이동 요청',
        SHIPPED: '이동중',
        ARRIVED: '도착완료',
        CANCELED: '이동취소',
      }[value] || '-'
    },
    displayStatusValue(item) {
      if (item.transferStatus === 'REQUESTED') return 'TRANSFER_REQUESTED'
      if (item.transferStatus === 'SHIPPED') return 'TRANSFERRING'
      if (item.transferStatus === 'ARRIVED') return 'READY_FOR_PICKUP'
      return item.storageStatus
    },
    displayStatusLabel(item) {
      const value = this.displayStatusValue(item)
      if (value === 'TRANSFER_REQUESTED') return '이동요청'
      if (value === 'READY_FOR_PICKUP') return '지점 수령완료'
      return this.statusLabel(value)
    },
    filterRequestItems(filter) {
      if (filter === 'NEEDS_ACTION') {
        return this.requestItems.filter(item => Boolean(this.nextStatusOption(item)))
      }
      if (filter === 'TRANSFER_REQUESTED') {
        return this.requestItems.filter(item => item.transferStatus === 'REQUESTED')
      }
      if (filter === 'TRANSFERRING') {
        return this.requestItems.filter(item => item.transferStatus === 'SHIPPED')
      }
      if (filter === 'READY_FOR_PICKUP') {
        return this.requestItems.filter(item => item.storageStatus === 'READY_FOR_PICKUP' || item.transferStatus === 'ARRIVED')
      }
      return this.requestItems
    },
    requestFilterCount(filter) {
      return this.filterRequestItems(filter).length
    },
    statusColor(value) {
      return {
        STORED: 'blue-grey',
        TRANSFER_REQUESTED: 'warning',
        PICKUP_REQUESTED: 'warning',
        DELIVERY_REQUESTED: 'warning',
        TRANSFERRING: 'primary',
        READY_FOR_PICKUP: 'success',
        PICKED_UP: 'success',
        SHIPPING: 'primary',
        DELIVERED: 'success',
        CANCELED: 'error',
      }[value] || 'blue-grey'
    },
    movementSummary(item) {
      if (!this.hasTransferTarget(item)) return '지점 이동이 필요하지 않습니다.'
      if (item.transferStatus === 'REQUESTED') return `${item.fromBranchName || item.currentBranchName || '-'}에서 이동 처리가 필요합니다.`
      if (item.transferStatus === 'SHIPPED') return `${item.toBranchName || item.targetBranchName || '-'} 도착 확인이 필요합니다.`
      if (item.transferStatus === 'ARRIVED') return `${item.toBranchName || item.targetBranchName || '-'}에 도착 처리되었습니다.`
      return '-'
    },
    movementFromName(item) {
      return item.fromBranchName || item.originalBranchName || item.currentBranchName || '-'
    },
    movementToName(item) {
      return item.toBranchName || item.targetBranchName || '-'
    },
    statusHistoryEntries(item) {
      const raw = String(item.statusHistoryText || '').trim()
      if (!raw) return []
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed.filter(Boolean)
      } catch (error) {
        return raw
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
          .map(line => ({ legacyText: line }))
      }
      return []
    },
    adminDisplay(entry) {
      const name = entry.adminName || '-'
      const loginId = entry.adminLoginId || ''
      if (!loginId || loginId === name) return name
      return `${name} (${loginId})`
    },
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    hasTransferTarget(item) {
      const target = item.toBranchCode || item.targetBranchCode
      const current = item.fromBranchCode || item.currentBranchCode
      return Boolean(target && current && target !== current)
    },
    isSendingBranch(item) {
      if (!this.hasTransferTarget(item)) return false
      if (this.isHq()) return true
      const branchCode = this.adminInfo.branchCode
      return Boolean(branchCode && (branchCode === item.fromBranchCode || branchCode === item.currentBranchCode))
    },
    isReceivingBranch(item) {
      if (!this.hasTransferTarget(item)) return false
      if (this.isHq()) return true
      const branchCode = this.adminInfo.branchCode
      return Boolean(branchCode && (branchCode === item.toBranchCode || branchCode === item.targetBranchCode))
    },
    statusOptions(item) {
      if (!this.hasTransferTarget(item)) return []

      if (
        this.isSendingBranch(item)
        && !['READY_FOR_PICKUP', 'PICKED_UP', 'SHIPPING', 'DELIVERED', 'CANCELED'].includes(item.storageStatus)
        && !['SHIPPED', 'ARRIVED'].includes(item.transferStatus)
      ) {
        const options = []
        if (item.storageStatus !== 'TRANSFERRING') {
          options.push({ title: this.statusLabel(item.storageStatus), value: item.storageStatus })
        }
        options.push({ title: '이동중', value: 'TRANSFERRING' })
        return options
      }

      if (this.isReceivingBranch(item) && item.storageStatus === 'TRANSFERRING' && item.transferStatus === 'SHIPPED') {
        if (item.requestType === 'DELIVERY') {
          return [{ title: '배송중', value: 'SHIPPING' }]
        }
        return [{ title: '지점 수령완료', value: 'READY_FOR_PICKUP' }]
      }

      return []
    },
    nextStatusOption(item) {
      if (this.isSendingBranch(item) && item.transferStatus === 'REQUESTED') {
        return this.statusOptions(item).find(option => option.value === 'TRANSFERRING') || null
      }
      return this.statusOptions(item).find(option => option.value !== item.storageStatus) || null
    },
    statusActionLabel(item) {
      const option = this.nextStatusOption(item)
      if (!option) return '처리 완료'
      if (option.value === 'TRANSFERRING') return '이동중 처리'
      if (option.value === 'READY_FOR_PICKUP') return '지점 수령완료 처리'
      if (option.value === 'SHIPPING') return '배송중 처리'
      return `${option.title} 처리`
    },
    statusActionDescription(item) {
      const option = this.nextStatusOption(item)
      if (!option) return '현재 처리 가능한 상태 변경이 없습니다.'
      if (option.value === 'TRANSFERRING') {
        return '상품을 타 지점으로 발송한 뒤 택배사, 송장번호, 전달사항 등을 메모로 남겨주세요.'
      }
      if (option.value === 'READY_FOR_PICKUP') {
        return '상품이 요청 지점에 도착했을 때 도착 확인 내용과 수령 준비 메모를 남겨주세요.'
      }
      if (option.value === 'SHIPPING') {
        return '배송을 시작한 뒤 택배사, 송장번호, 전달사항 등을 메모로 남겨주세요.'
      }
      return '처리 내용을 메모로 남겨주세요.'
    },
    openStatusDialog(item) {
      const option = this.nextStatusOption(item)
      if (!option) return
      this.statusDialog = {
        open: true,
        item,
        status: option.value,
        memo: '',
      }
    },
    closeStatusDialog() {
      this.statusDialog = {
        open: false,
        item: null,
        status: '',
        memo: '',
      }
    },
    async saveDialogStatus() {
      const item = this.statusDialog.item
      if (!item || !this.statusDialog.status) return
      this.savingCode = item.storageItemCode
      try {
        const { data } = await updateStorageStatus(item.storageItemCode, {
          storageStatus: this.statusDialog.status,
          memo: this.statusDialog.memo,
        })
        if (data.code !== 0) {
          window.alert(data.message || '상태 변경에 실패했습니다.')
          return
        }
        this.closeStatusDialog()
        await this.load()
      } catch (error) {
        window.alert(error.response?.data?.message || '상태 변경 중 오류가 발생했습니다.')
      } finally {
        this.savingCode = ''
      }
    },
  },
}
</script>

<template>
  <div class="storage-page">
    <div class="page-header">
      <div>
        <h1>보관/이동 관리</h1>
        <p>회원 보관 상품과 수령/배송 신청, 지점 이동이 필요한 건을 확인합니다.</p>
      </div>
      <v-btn color="primary" variant="tonal" :loading="loading" @click="load">새로고침</v-btn>
    </div>

    <v-row class="mb-5">
      <v-col cols="12" sm="6" lg="2">
        <v-card class="summary-card" rounded="lg">
          <span>보관중</span>
          <strong>{{ fmtNum(storedItems.length) }}</strong>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="2">
        <v-card class="summary-card urgent" rounded="lg">
          <span>처리필요</span>
          <strong>{{ fmtNum(requestFilterCount('NEEDS_ACTION')) }}</strong>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="2">
        <v-card class="summary-card urgent" rounded="lg">
          <span>이동요청</span>
          <strong>{{ fmtNum(requestFilterCount('TRANSFER_REQUESTED')) }}</strong>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="2">
        <v-card class="summary-card urgent" rounded="lg">
          <span>이동중</span>
          <strong>{{ fmtNum(requestFilterCount('TRANSFERRING')) }}</strong>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="2">
        <v-card class="summary-card ready" rounded="lg">
          <span>지점 수령완료</span>
          <strong>{{ fmtNum(requestFilterCount('READY_FOR_PICKUP')) }}</strong>
        </v-card>
      </v-col>
    </v-row>

    <v-card rounded="lg" class="storage-list-card">
      <div class="storage-tabs">
        <button
          type="button"
          :class="{ active: activeStorageTab === 'requests' }"
          @click="activeStorageTab = 'requests'"
        >
          이동/수령 요청
          <em>{{ fmtNum(requestItems.length) }}</em>
        </button>
        <button
          type="button"
          :class="{ active: activeStorageTab === 'stored' }"
          @click="activeStorageTab = 'stored'"
        >
          보관중 상품
          <em>{{ fmtNum(storedItems.length) }}</em>
        </button>
      </div>

      <template v-if="activeStorageTab === 'requests'">
        <v-card-title class="section-title">
          <span>이동/수령 요청 목록</span>
          <v-chip size="small" variant="tonal" color="primary">{{ fmtNum(filteredRequestItems.length) }}건</v-chip>
        </v-card-title>

        <div class="request-filters">
          <button type="button" :class="{ active: requestStatusFilter === 'ALL' }" @click="requestStatusFilter = 'ALL'">
            전체 <em>{{ fmtNum(requestFilterCount('ALL')) }}</em>
          </button>
          <button type="button" :class="{ active: requestStatusFilter === 'NEEDS_ACTION' }" @click="requestStatusFilter = 'NEEDS_ACTION'">
            처리필요 <em>{{ fmtNum(requestFilterCount('NEEDS_ACTION')) }}</em>
          </button>
          <button type="button" :class="{ active: requestStatusFilter === 'TRANSFER_REQUESTED' }" @click="requestStatusFilter = 'TRANSFER_REQUESTED'">
            이동요청 <em>{{ fmtNum(requestFilterCount('TRANSFER_REQUESTED')) }}</em>
          </button>
          <button type="button" :class="{ active: requestStatusFilter === 'TRANSFERRING' }" @click="requestStatusFilter = 'TRANSFERRING'">
            이동중 <em>{{ fmtNum(requestFilterCount('TRANSFERRING')) }}</em>
          </button>
          <button type="button" :class="{ active: requestStatusFilter === 'READY_FOR_PICKUP' }" @click="requestStatusFilter = 'READY_FOR_PICKUP'">
            지점 수령완료 <em>{{ fmtNum(requestFilterCount('READY_FOR_PICKUP')) }}</em>
          </button>
        </div>

        <div v-if="!requestItems.length" class="empty-state">이동/수령 요청이 들어온 보관 상품이 없습니다.</div>
        <div v-else-if="!filteredRequestItems.length" class="empty-state">선택한 상태의 이동/수령 요청이 없습니다.</div>

        <div v-else class="storage-card-list">
        <article
          v-for="item in filteredRequestItems"
          :key="item.storageItemCode"
          class="storage-item-card"
        >
          <div class="card-main">
            <div class="card-header">
              <div class="chip-row">
                <v-chip size="small" :color="statusColor(displayStatusValue(item))" variant="tonal">
                  {{ displayStatusLabel(item) }}
                </v-chip>
              </div>
              <div class="amount">{{ fmtWon(item.itemAmount) }}</div>
            </div>

            <div class="product-title">{{ item.productName }}</div>
            <div class="order-code">{{ item.orderCode }}</div>

            <div class="info-grid">
              <div>
                <span>회원</span>
                <strong>{{ item.userName || '-' }}</strong>
                <em>{{ item.userEmail || '-' }}</em>
              </div>
              <div>
                <span>보관/이동 경로</span>
                <strong>{{ movementFromName(item) }} → {{ movementToName(item) }}</strong>
                <em>원 보관 지점 {{ item.originalBranchName || '-' }}</em>
              </div>
              <div>
                <span>이동 상태</span>
                <strong>{{ transferStatusLabel(item.transferStatus) }}</strong>
                <em>{{ movementSummary(item) }}</em>
              </div>
            </div>

            <div class="memo-box">
              <span>처리 이력</span>
              <ul v-if="statusHistoryEntries(item).length" class="history-list">
                <li v-for="(entry, index) in statusHistoryEntries(item)" :key="`${entry.createdDt || index}-${entry.statusLabel || entry.legacyText}`">
                  <template v-if="entry.legacyText">
                    {{ entry.legacyText }}
                  </template>
                  <template v-else>
                    <div class="history-head">
                      <strong>{{ entry.branchRole }} · {{ entry.branchName }}</strong>
                      <span>{{ entry.createdDt }}</span>
                    </div>
                    <div class="history-meta">
                      <span>{{ entry.statusLabel }}</span>
                      <em>담당자 {{ adminDisplay(entry) }}</em>
                    </div>
                    <p v-if="entry.memo">{{ entry.memo }}</p>
                  </template>
                </li>
              </ul>
              <p v-else class="empty-memo">등록된 처리 메모가 없습니다.</p>
            </div>
          </div>

          <div class="card-actions" @click.stop>
            <div class="side-panel">
              <div class="side-block current-block">
                <span>현재 처리</span>
                <strong>{{ editableStatusLabel(item.storageStatus, item) }}</strong>
              </div>
              <div class="side-block">
                <span>주문 정보</span>
                <dl>
                  <dt>주문번호</dt>
                  <dd>{{ item.orderCode || '-' }}</dd>
                  <dt>주문일</dt>
                  <dd>{{ fmtDateTime(item.orderedDt) }}</dd>
                  <dt>결제일</dt>
                  <dd>{{ fmtDateTime(item.paidDt) }}</dd>
                  <dt>결제수단</dt>
                  <dd>{{ paymentTypeLabel(item.paymentType) }}</dd>
                  <dt>결제상태</dt>
                  <dd>{{ paymentStatusLabel(item.paymentStatus) }}</dd>
                </dl>
              </div>
              <div class="side-block">
                <span>회원 요청</span>
                <dl>
                  <dt>요청유형</dt>
                  <dd>{{ requestTypeLabel(item.requestType) }}</dd>
                  <dt>요청일</dt>
                  <dd>{{ fmtDateTime(item.lastRequestedDt || item.storageRequestedDt) }}</dd>
                  <dt>예약일</dt>
                  <dd>{{ fmtDate(item.expectedReceiptDate) }}</dd>
                  <dt>요청 지점</dt>
                  <dd>{{ item.targetBranchName || '-' }}</dd>
                </dl>
                <div v-if="item.requestMemo" class="request-memo-box">
                  <span>회원 요청 메모</span>
                  <p>{{ item.requestMemo }}</p>
                </div>
              </div>
              <div class="side-block">
                <span>보관 상품 정보</span>
                <dl>
                  <dt>보관 지점</dt>
                  <dd>{{ item.currentBranchName || '-' }}</dd>
                  <dt>원 보관</dt>
                  <dd>{{ item.originalBranchName || '-' }}</dd>
                  <dt>이동 상태</dt>
                  <dd>{{ transferStatusLabel(item.transferStatus) }}</dd>
                  <dt>상품금액</dt>
                  <dd>{{ fmtWon(item.itemAmount) }}</dd>
                </dl>
              </div>
              <div v-if="nextStatusOption(item)" class="side-block action-block">
                <span>상태 처리</span>
                <strong>{{ statusActionLabel(item) }}</strong>
                <p>{{ statusActionDescription(item) }}</p>
                <v-btn
                  color="primary"
                  block
                  :loading="savingCode === item.storageItemCode"
                  @click="openStatusDialog(item)"
                >
                  {{ statusActionLabel(item) }}
                </v-btn>
              </div>
            </div>
          </div>
        </article>
        </div>
      </template>

      <template v-else>
        <v-card-title class="section-title">
          <span>보관중 상품 목록</span>
          <v-chip size="small" variant="tonal" color="blue-grey">{{ fmtNum(storedItems.length) }}건</v-chip>
        </v-card-title>

        <div v-if="!storedItems.length" class="empty-state">현재 보관중인 상품이 없습니다.</div>

        <div v-else class="storage-card-list compact">
        <article
          v-for="item in storedItems"
          :key="item.storageItemCode"
          class="storage-item-card stored-card"
        >
          <div class="card-main">
            <div class="card-header">
              <v-chip size="small" :color="statusColor(displayStatusValue(item))" variant="tonal">
                {{ displayStatusLabel(item) }}
              </v-chip>
              <div class="amount">{{ fmtWon(item.itemAmount) }}</div>
            </div>

            <div class="product-title">{{ item.productName }}</div>
            <div class="order-code">{{ item.orderCode }}</div>

            <div class="info-grid">
              <div>
                <span>회원</span>
                <strong>{{ item.userName || '-' }}</strong>
                <em>{{ item.userEmail || '-' }}</em>
              </div>
              <div>
                <span>보관 지점</span>
                <strong>{{ item.currentBranchName || '-' }}</strong>
                <em>원 보관 지점 {{ item.originalBranchName || '-' }}</em>
              </div>
              <div>
                <span>주문/결제</span>
                <strong>{{ paymentStatusLabel(item.paymentStatus) }}</strong>
                <em>{{ fmtDateTime(item.paidDt || item.orderedDt) }}</em>
              </div>
            </div>
          </div>

          <div class="card-actions readonly-only">
            <div class="side-panel">
              <div class="side-block current-block">
                <span>현재 처리</span>
                <strong>회원 수령/배송 신청 전</strong>
              </div>
              <div class="side-block">
                <span>주문 정보</span>
                <dl>
                  <dt>주문번호</dt>
                  <dd>{{ item.orderCode || '-' }}</dd>
                  <dt>주문일</dt>
                  <dd>{{ fmtDateTime(item.orderedDt) }}</dd>
                  <dt>결제일</dt>
                  <dd>{{ fmtDateTime(item.paidDt) }}</dd>
                  <dt>결제수단</dt>
                  <dd>{{ paymentTypeLabel(item.paymentType) }}</dd>
                  <dt>결제상태</dt>
                  <dd>{{ paymentStatusLabel(item.paymentStatus) }}</dd>
                </dl>
              </div>
            </div>
          </div>
        </article>
        </div>
      </template>
    </v-card>

    <div v-if="!hasAnyItems" class="empty-state">보관/이동 관리 대상이 없습니다.</div>

    <v-dialog v-model="statusDialog.open" max-width="560">
      <v-card class="status-dialog-card" rounded="lg">
        <v-card-title>{{ statusDialog.item ? statusActionLabel(statusDialog.item) : '상태 처리' }}</v-card-title>
        <v-card-text>
          <p class="dialog-desc">{{ statusDialog.item ? statusActionDescription(statusDialog.item) : '' }}</p>
          <div v-if="statusDialog.item" class="dialog-summary">
            <span>상품</span>
            <strong>{{ statusDialog.item.productName }}</strong>
            <em>{{ movementFromName(statusDialog.item) }} → {{ movementToName(statusDialog.item) }}</em>
          </div>
          <v-textarea
            v-model="statusDialog.memo"
            class="mt-4"
            variant="outlined"
            rows="7"
            auto-grow
            hide-details
            placeholder="처리 메모를 입력하세요. 예: 택배사, 송장번호, 전달사항, 도착 확인 내용"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeStatusDialog">취소</v-btn>
          <v-btn
            color="primary"
            :loading="statusDialog.item && savingCode === statusDialog.item.storageItemCode"
            @click="saveDialogStatus"
          >
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.page-header h1 {
  margin: 0;
  color: #fff;
  font-size: 24px;
  font-weight: 800;
}

.page-header p {
  margin: 6px 0 0;
  color: #aeb8c8;
  font-size: 14px;
}

.summary-card {
  padding: 18px;
  background: #1f2633;
  border: 1px solid #333c4d;
  color: #dbe4f2;
}

.summary-card span {
  display: block;
  font-size: 13px;
  color: #9ba7b9;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 26px;
  color: #fff;
}

.summary-card.urgent {
  border-color: #d7b86d;
}

.summary-card.ready {
  border-color: #3e6fb3;
}

.storage-list-card {
  background: #1b202b;
  border: 1px solid #343d4f;
}

.storage-tabs {
  display: flex;
  gap: 0;
  padding: 12px 12px 0;
  border-bottom: 1px solid #343d4f;
}

.storage-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 168px;
  padding: 14px 18px;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: 8px 8px 0 0;
  background: transparent;
  color: #9ba7b9;
  font-size: 14px;
  font-weight: 800;
}

.storage-tabs button.active {
  border-color: #343d4f;
  background: #232a38;
  color: #f2d486;
  box-shadow: inset 0 -2px 0 #d7b86d;
}

.storage-tabs em {
  display: inline-flex;
  min-width: 28px;
  height: 22px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.16);
  color: #fff;
  font-size: 12px;
  font-style: normal;
}

.storage-tabs button.active em {
  background: rgba(215, 184, 109, 0.2);
  color: #f2d486;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 800;
}

.request-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 14px;
}

.request-filters button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #394357;
  border-radius: 999px;
  background: #151b26;
  color: #aeb8c8;
  font-size: 13px;
  font-weight: 800;
}

.request-filters button.active {
  border-color: rgba(215, 184, 109, 0.8);
  background: rgba(215, 184, 109, 0.13);
  color: #f2d486;
}

.request-filters em {
  min-width: 22px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.16);
  color: #fff;
  font-size: 12px;
  font-style: normal;
}

.empty-state {
  padding: 36px 20px;
  color: #9ba7b9;
  text-align: center;
}

.storage-card-list {
  display: grid;
  gap: 14px;
  padding: 0 16px 18px;
}

.storage-card-list.compact {
  gap: 10px;
}

.storage-item-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 460px);
  gap: 18px;
  padding: 18px;
  border: 1px solid #333c4d;
  border-radius: 8px;
  background: #131923;
}

.storage-item-card:hover {
  border-color: rgba(215, 184, 109, 0.7);
  background: #171e2a;
}

.stored-card {
  border-color: #2f394a;
}

.card-header,
.chip-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header {
  justify-content: space-between;
  margin-bottom: 12px;
}

.amount {
  color: #fff;
  font-weight: 800;
}

.product-title {
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.45;
}

.order-code {
  margin-top: 4px;
  color: #9ba7b9;
  font-size: 12px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.info-grid div,
.memo-box,
.readonly-action {
  padding: 12px;
  border-radius: 8px;
  background: #202736;
}

.info-grid span,
.memo-box span,
.readonly-action span {
  display: block;
  color: #9ba7b9;
  font-size: 12px;
  font-weight: 700;
}

.info-grid strong,
.readonly-action strong {
  display: block;
  margin-top: 6px;
  color: #fff;
  font-size: 14px;
}

.info-grid em {
  display: block;
  margin-top: 4px;
  color: #aeb8c8;
  font-size: 12px;
  font-style: normal;
}

.memo-box {
  margin-top: 12px;
}

.memo-box p {
  margin: 7px 0 0;
  color: #fff;
  white-space: pre-wrap;
}

.history-list {
  display: grid;
  gap: 8px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
}

.history-list li {
  padding: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.35);
  color: #dbeafe;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
}

.history-head,
.history-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.history-head strong {
  color: #fff;
  font-size: 13px;
}

.history-head span {
  color: #93a4ba;
  font-size: 12px;
  white-space: nowrap;
}

.history-meta {
  margin-top: 6px;
}

.history-meta span {
  display: inline-flex;
  width: fit-content;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(215, 184, 109, 0.16);
  color: #f2d486;
  font-size: 12px;
  font-weight: 800;
}

.history-meta em {
  color: #aeb8c8;
  font-size: 12px;
  font-style: normal;
  text-align: right;
}

.history-list p {
  margin: 8px 0 0;
  color: #fff;
}

.memo-box .empty-memo {
  color: #7f8a9c;
}

.memo-box small {
  display: block;
  margin-top: 8px;
  color: #9ba7b9;
}

.card-actions {
  display: grid;
  gap: 12px;
  align-content: start;
  min-height: 100%;
}

.side-panel {
  display: grid;
  gap: 10px;
  height: 100%;
  grid-template-rows: auto auto auto minmax(112px, 1fr);
}

.side-block {
  padding: 12px;
  border-radius: 8px;
  background: #202736;
}

.side-block.current-block {
  border: 1px solid rgba(215, 184, 109, 0.18);
  background: rgba(215, 184, 109, 0.08);
}

.side-block span {
  display: block;
  color: #9ba7b9;
  font-size: 12px;
  font-weight: 800;
}

.side-block strong {
  display: block;
  margin-top: 6px;
  color: #fff;
  font-size: 14px;
}

.side-block dl {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 6px 10px;
  margin: 8px 0 0;
}

.side-block dt {
  color: #8fa0b6;
  font-size: 12px;
  font-weight: 700;
}

.side-block dd {
  min-width: 0;
  margin: 0;
  color: #e5edf8;
  font-size: 12px;
  word-break: break-all;
}

.request-memo-box {
  margin-top: 10px;
  padding: 10px;
  border: 1px solid rgba(215, 184, 109, 0.16);
  border-radius: 8px;
  background: rgba(215, 184, 109, 0.08);
}

.request-memo-box span {
  color: #d7b86d;
  font-size: 12px;
  font-weight: 800;
}

.request-memo-box p {
  margin: 6px 0 0;
  color: #f4f7fb;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.action-block {
  display: flex;
  min-height: 112px;
  flex-direction: column;
}

.action-block p {
  margin: 8px 0 12px;
  color: #aeb8c8;
  font-size: 12px;
  line-height: 1.5;
}

.action-block .v-btn {
  margin-top: auto;
}

.status-edit-panel {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 72px;
  gap: 10px;
  align-items: center;
}

.readonly-only {
  align-content: stretch;
}

.status-dialog-card {
  border: 1px solid #343d4f;
  background: #1b202b;
  color: #fff;
}

.dialog-desc {
  margin: 0 0 12px;
  color: #aeb8c8;
  font-size: 13px;
  line-height: 1.5;
}

.dialog-summary {
  padding: 12px;
  border-radius: 8px;
  background: #202736;
}

.dialog-summary span,
.dialog-summary em {
  display: block;
  color: #9ba7b9;
  font-size: 12px;
  font-style: normal;
}

.dialog-summary strong {
  display: block;
  margin: 5px 0;
  color: #fff;
  font-size: 14px;
}

@media (max-width: 1180px) {
  .storage-item-card {
    grid-template-columns: 1fr;
  }

  .side-panel {
    grid-template-rows: none;
  }
}

@media (max-width: 760px) {
  .info-grid,
  .card-actions,
  .status-edit-panel {
    grid-template-columns: 1fr;
  }
}
</style>
